import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { PaEmptyState, PaPagination, PaSkeleton } from "components/platform-admin";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import { fetchPlatformAuditLog } from "store/services/platformAdmin.services";
import PlatformAuditLogTable from "../Audit/PlatformAuditLogTable";
import {
  buildAuditLogQuery,
  createDefaultAuditFilters,
  formatUserLabel,
  hasExpandableMetadata,
} from "../Audit/auditLog.utils";

const TenantActivityTab = ({ tenantId, tenantName }) => {
  const canViewAudit = platformRoleAccess("platform.audit.view");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [filters, setFilters] = useState(() => ({
    ...createDefaultAuditFilters(),
    spId: tenantId ? String(tenantId) : "",
    perPage: 15,
  }));
  const [pagination, setPagination] = useState(getPaginationObj());

  const loadActivity = useCallback(async () => {
    if (!canViewAudit || !tenantId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildAuditLogQuery(filters);
      const data = await fetchPlatformAuditLog(endpoint);
      setRows(data?.audit_log?.data || []);
      setPagination(getPaginationObj(data?.audit_log, filters));
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load tenant activity");
    } finally {
      setLoading(false);
    }
  }, [canViewAudit, filters, tenantId]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      spId: tenantId ? String(tenantId) : "",
      page: 1,
    }));
  }, [tenantId]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const columns = useMemo(
    () => [
      {
        key: "created_at",
        label: "When",
        render: (row) =>
          row.created_at ? (
            <time dateTime={row.created_at} title={moment(row.created_at).format("D MMM YYYY, h:mm a")}>
              {moment(row.created_at).fromNow()}
            </time>
          ) : (
            "—"
          ),
      },
      {
        key: "action",
        label: "Event",
        render: (row) => (
          <div>
            <p className="font-medium text-gray-900">{row.description || row.action || "Activity"}</p>
            {row.action ? <p className="text-xs text-gray-500">{row.action}</p> : null}
          </div>
        ),
      },
      {
        key: "admin",
        label: "Actor",
        render: (row) => formatUserLabel(row.admin || row.user),
      },
      {
        key: "expand",
        label: "",
        headerClassName: "w-12",
        render: (row) =>
          hasExpandableMetadata(row.metadata) ? (
            <button
              type="button"
              onClick={() => setExpandedRowId((current) => (current === row.id ? null : row.id))}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              {expandedRowId === row.id ? "Hide" : "Details"}
            </button>
          ) : (
            "—"
          ),
      },
    ],
    [expandedRowId]
  );

  if (!canViewAudit) {
    return (
      <PaEmptyState
        title="Access restricted"
        message="You need platform.audit.view to see tenant activity."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Platform and tenant-scoped actions for {tenantName || "this tenant"} (last 14 days).
        </p>
        <Link
          to={`/audit-log?sp_id=${tenantId}`}
          className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
        >
          Open full audit log →
        </Link>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <PlatformAuditLogTable
        columns={columns}
        rows={rows}
        expandedRowId={expandedRowId}
        onToggleExpand={setExpandedRowId}
        isLoading={loading}
        emptyState={
          <PaEmptyState
            title="No activity yet"
            message="Platform actions for this tenant will appear here."
          />
        }
      />

      {!loading && rows.length > 0 ? (
        <PaPagination
          page={filters.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          rows={rows}
          perPage={filters.perPage}
          loading={loading}
          onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
        />
      ) : null}

      {loading ? <PaSkeleton variant="text" lines={2} /> : null}
    </div>
  );
};

export default TenantActivityTab;
